import { create } from 'zustand';
import type {
  Car,
  ChannelConfigEntry,
  ChannelPreset,
  Driver,
  Event,
  GraphMode,
  LapRef,
  MapColourMode,
  ResampledLap,
  Session,
  SessionSummary,
  Track,
  TrackCorner,
  TrackGate,
  ViewState,
} from '@tla/types';
import { api } from '../api/client';
import { channelUnion, evictSession, loadSession, resampleLapRef } from '../session/sessionCache';
import { lapKey } from './colours';
import { useToastStore } from './toastStore';

/** Channels shown by default, in stack order, when a workspace is first built. */
const DEFAULT_VISIBLE: string[] = [
  'speed_gps',
  'brake',
  'throttle_pos',
  'rpm',
  'longitudinal_acc',
  'lateral_acc',
];

/**
 * Channels the lap-events timeline needs to classify braking / cornering /
 * straight segments. These are always resampled even when hidden from the
 * graph stack so the timeline keeps working.
 */
const SEGMENT_CHANNELS = ['longitudinal_acc', 'lateral_acc', 'speed_gps'];

/** Maximum number of laps (reference + comparisons) allowed in the workspace. */
export const MAX_LAPS = 20;

interface EntityMaps {
  tracks: Record<string, Track>;
  drivers: Record<string, Driver>;
  cars: Record<string, Car>;
  events: Record<string, Event>;
}

interface WorkspaceState extends EntityMaps {
  /** Ordered event ids for the browser (most recent first). */
  eventOrder: string[];
  selectedEventId: string | null;
  /** Session summaries keyed by session id (lazily loaded). */
  summaries: Record<string, SessionSummary>;
  /** Session records keyed by session id, cached as laps are loaded. */
  sessions: Record<string, Session>;
  presets: ChannelPreset[];

  // View state (the shareable workspace)
  trackId: string | null;
  referenceLap: LapRef | null;
  comparisonLaps: LapRef[];
  /** lapKey values that are temporarily hidden from the graphs, map & timeline. */
  hiddenLaps: string[];
  zoom: [number, number] | null;
  highlight: [number, number] | null;
  channelConfig: ChannelConfigEntry[];
  graphMode: GraphMode;
  /** In tiled mode, lay the per-channel plots out in a 2-column grid. */
  graphGrid: boolean;
  mapColourMode: MapColourMode;
  /** Whether to draw named corners on the track map. */
  showCornerNames: boolean;

  /** Channel metadata (name/unit/group) keyed by channel id. */
  channelMeta: Record<string, { name: string; unit: string; group: string }>;

  /** Resampled laps keyed by lapKey(ref), on the shared distance grid. */
  resampled: Record<string, ResampledLap>;

  status: string | null;
  error: string | null;

  // Actions
  init: () => Promise<void>;
  refreshEntities: () => Promise<void>;
  selectEvent: (eventId: string | null) => Promise<void>;
  ensureSummary: (sessionId: string) => Promise<void>;
  setReferenceLap: (ref: LapRef) => Promise<void>;
  addComparisonLap: (ref: LapRef) => Promise<void>;
  removeLap: (ref: LapRef) => void;
  toggleLapHidden: (ref: LapRef) => void;
  clearLaps: () => void;
  removeSession: (sessionId: string) => Promise<void>;

  setChannelVisible: (channelId: string, visible: boolean) => void;
  reorderChannel: (channelId: string, direction: -1 | 1) => void;
  setChannelColour: (channelId: string, colour: string) => void;
  setGraphMode: (mode: GraphMode) => void;
  setGraphGrid: (v: boolean) => void;
  setMapColourMode: (mode: MapColourMode) => void;
  setShowCornerNames: (v: boolean) => void;
  /** Persist the current track's named corners (shared across the team). */
  saveTrackCorners: (corners: TrackCorner[]) => Promise<void>;
  /** Persist track editor changes (corners and/or sector gates). */
  saveTrackConfig: (patch: { corners?: TrackCorner[]; sectors?: TrackGate[] }) => Promise<void>;
  applyPreset: (preset: ChannelPreset) => void;
  savePreset: (name: string) => Promise<void>;
  deletePreset: (id: string) => Promise<void>;

  setZoom: (range: [number, number] | null) => void;
  setHighlight: (range: [number, number] | null) => void;
  loadViewState: (view: ViewState) => Promise<void>;
  currentViewState: () => ViewState | null;
}

/** All lap references currently in the workspace (reference first). */
function allLaps(state: WorkspaceState): LapRef[] {
  return state.referenceLap ? [state.referenceLap, ...state.comparisonLaps] : state.comparisonLaps;
}

/** Visible channel ids in stack order. */
function visibleChannelIds(config: ChannelConfigEntry[]): string[] {
  return [...config]
    .filter((c) => c.visible)
    .sort((a, b) => a.order - b.order)
    .map((c) => c.channelId);
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  tracks: {},
  drivers: {},
  cars: {},
  events: {},
  eventOrder: [],
  selectedEventId: null,
  summaries: {},
  sessions: {},
  presets: [],

  trackId: null,
  referenceLap: null,
  comparisonLaps: [],
  hiddenLaps: [],
  zoom: null,
  highlight: null,
  channelConfig: [],
  graphMode: 'tiled',
  graphGrid: true,
  mapColourMode: 'longitudinal_g',
  showCornerNames: true,

  channelMeta: {},
  resampled: {},
  status: null,
  error: null,

  init: async () => {
    await get().refreshEntities();
    try {
      const presets = await api.listPresets();
      set({ presets });
    } catch {
      /* presets are optional */
    }
  },

  refreshEntities: async () => {
    try {
      const [tracks, drivers, cars, events] = await Promise.all([
        api.listTracks(),
        api.listDrivers(),
        api.listCars(),
        api.listEvents(),
      ]);
      set({
        tracks: Object.fromEntries(tracks.map((t) => [t.id, t])),
        drivers: Object.fromEntries(drivers.map((d) => [d.id, d])),
        cars: Object.fromEntries(cars.map((c) => [c.id, c])),
        events: Object.fromEntries(events.map((e) => [e.id, e])),
        eventOrder: events
          .slice()
          .sort((a, b) => b.date.localeCompare(a.date))
          .map((e) => e.id),
        error: null,
      });
    } catch (err) {
      set({ error: `Could not reach the API: ${(err as Error).message}` });
    }
  },

  selectEvent: async (eventId) => {
    set({ selectedEventId: eventId });
  },

  ensureSummary: async (sessionId) => {
    if (get().summaries[sessionId]) return;
    try {
      const summary = await api.getSessionSummary(sessionId);
      set((s) => ({ summaries: { ...s.summaries, [sessionId]: summary } }));
    } catch (err) {
      set({ error: `Failed to load session summary: ${(err as Error).message}` });
    }
  },

  setReferenceLap: async (ref) => {
    try {
      const session = await api.getSession(ref.sessionId);
      // Setting a new reference resets comparisons and locks the workspace track.
      set((s) => ({
        referenceLap: ref,
        comparisonLaps: [],
        trackId: session.trackId,
        sessions: { ...s.sessions, [session.id]: session },
        zoom: null,
        highlight: null,
        error: null,
      }));
      await ensureChannelConfig(get, set);
      await resampleAll(get, set);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  addComparisonLap: async (ref) => {
    const state = get();
    if (!state.referenceLap) {
      await state.setReferenceLap(ref);
      return;
    }
    // Guard: comparisons must be on the same track as the reference.
    try {
      const session = await api.getSession(ref.sessionId);
      if (state.trackId && session.trackId !== state.trackId) {
        useToastStore.getState().showToast(
          'That lap is from a different track and cannot be compared here.',
          'error',
        );
        return;
      }
      const key = lapKey(ref);
      const exists = allLaps(state).some((l) => lapKey(l) === key);
      if (exists) return;
      if (allLaps(state).length >= MAX_LAPS) {
        useToastStore
          .getState()
          .showToast(`You can compare at most ${MAX_LAPS} laps. Remove one to add another.`, 'error');
        return;
      }
      set((s) => ({
        comparisonLaps: [...s.comparisonLaps, ref],
        sessions: { ...s.sessions, [session.id]: session },
        error: null,
      }));
      await resampleAll(get, set);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  removeLap: (ref) => {
    const key = lapKey(ref);
    const state = get();
    set({ hiddenLaps: state.hiddenLaps.filter((k) => k !== key) });
    if (state.referenceLap && lapKey(state.referenceLap) === key) {
      // Promote the first comparison lap to reference, if any.
      const [next, ...rest] = state.comparisonLaps;
      set({ referenceLap: next ?? null, comparisonLaps: rest });
    } else {
      set({ comparisonLaps: state.comparisonLaps.filter((l) => lapKey(l) !== key) });
    }
    if (!get().referenceLap && get().comparisonLaps.length === 0) {
      set({ trackId: null, resampled: {} });
    } else {
      void resampleAll(get, set);
    }
  },

  toggleLapHidden: (ref) => {
    const key = lapKey(ref);
    set((s) => ({
      hiddenLaps: s.hiddenLaps.includes(key)
        ? s.hiddenLaps.filter((k) => k !== key)
        : [...s.hiddenLaps, key],
    }));
  },

  clearLaps: () => {
    set({ referenceLap: null, comparisonLaps: [], resampled: {}, trackId: null, zoom: null, hiddenLaps: [] });
  },

  removeSession: async (sessionId) => {
    try {
      await api.deleteSession(sessionId);
      evictSession(sessionId);
      const state = get();
      // Drop any laps referencing the deleted session.
      const keepComparisons = state.comparisonLaps.filter((l) => l.sessionId !== sessionId);
      const refGone = state.referenceLap?.sessionId === sessionId;
      set({
        comparisonLaps: keepComparisons,
        referenceLap: refGone ? null : state.referenceLap,
        summaries: Object.fromEntries(
          Object.entries(state.summaries).filter(([id]) => id !== sessionId),
        ),
      });
      if (refGone) get().clearLaps();
      await get().refreshEntities();
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  setChannelVisible: (channelId, visible) => {
    set((s) => ({
      channelConfig: s.channelConfig.map((c) =>
        c.channelId === channelId ? { ...c, visible } : c,
      ),
    }));
    void resampleAll(get, set);
  },

  reorderChannel: (channelId, direction) => {
    const config = [...get().channelConfig].sort((a, b) => a.order - b.order);
    const idx = config.findIndex((c) => c.channelId === channelId);
    const swap = idx + direction;
    if (idx < 0 || swap < 0 || swap >= config.length) return;
    const a = config[idx];
    const b = config[swap];
    const tmp = a.order;
    a.order = b.order;
    b.order = tmp;
    set({ channelConfig: config });
  },

  setChannelColour: (channelId, colour) => {
    set((s) => ({
      channelConfig: s.channelConfig.map((c) =>
        c.channelId === channelId ? { ...c, colour } : c,
      ),
    }));
  },

  setGraphMode: (graphMode) => set({ graphMode }),
  setGraphGrid: (graphGrid) => set({ graphGrid }),
  setMapColourMode: (mapColourMode) => set({ mapColourMode }),
  setShowCornerNames: (showCornerNames) => set({ showCornerNames }),

  saveTrackCorners: async (corners) => {
    await get().saveTrackConfig({ corners });
  },

  saveTrackConfig: async (patch) => {
    const id = get().trackId;
    if (!id) return;
    const track = get().tracks[id];
    if (!track) return;
    const updated: Track = {
      ...track,
      ...(patch.corners !== undefined ? { corners: patch.corners } : {}),
      ...(patch.sectors !== undefined ? { sectors: patch.sectors } : {}),
    };
    try {
      const saved = await api.updateTrack(id, updated);
      // Sector-gate changes affect computed sector times, so drop cached
      // summaries for this track's loaded sessions and refetch them.
      const affected =
        patch.sectors !== undefined
          ? Object.values(get().sessions)
              .filter((s) => s.trackId === id)
              .map((s) => s.id)
          : [];
      set((s) => {
        const summaries = { ...s.summaries };
        for (const sid of affected) delete summaries[sid];
        return { tracks: { ...s.tracks, [id]: saved }, summaries };
      });
      await Promise.all(affected.map((sid) => get().ensureSummary(sid)));
    } catch (err) {
      set({ error: `Failed to save track: ${(err as Error).message}` });
    }
  },

  applyPreset: (preset) => {
    set({ channelConfig: preset.entries.map((e) => ({ ...e })), graphMode: preset.graphMode });
    void resampleAll(get, set);
  },

  savePreset: async (name) => {
    try {
      const preset = await api.createPreset({
        name,
        entries: get().channelConfig,
        graphMode: get().graphMode,
      });
      set((s) => ({ presets: [...s.presets, preset] }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  deletePreset: async (id) => {
    try {
      await api.deletePreset(id);
      set((s) => ({ presets: s.presets.filter((p) => p.id !== id) }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  setZoom: (zoom) => set({ zoom }),
  setHighlight: (highlight) => set({ highlight }),

  loadViewState: async (view) => {
    set({
      trackId: view.trackId,
      graphMode: view.graphMode,
      mapColourMode: view.mapColourMode,
      channelConfig: view.channelConfig.map((e) => ({ ...e })),
      zoom: view.zoom,
      highlight: view.highlight,
    });
    if (!view.referenceLap) return;

    // Deduplicate laps (reference first, then comparisons) so a share can never
    // produce duplicate lap chips/keys, and apply them in a single atomic state
    // update. This is idempotent: if loadViewState runs more than once (e.g.
    // React StrictMode invokes the loader effect twice in dev, or a link is
    // opened twice), the result is identical — no laps are appended twice, which
    // previously corrupted the workspace and left the track map unable to draw.
    const ordered: LapRef[] = [];
    const seen = new Set<string>();
    for (const lap of [view.referenceLap, ...view.comparisonLaps]) {
      const k = lapKey(lap);
      if (seen.has(k)) continue;
      seen.add(k);
      ordered.push(lap);
    }
    const [reference, ...comparisons] = ordered;
    const sessionIds = [...new Set(ordered.map((l) => l.sessionId))];
    try {
      const loaded = await Promise.all(sessionIds.map((id) => api.getSession(id)));
      const sessions: Record<string, Session> = {};
      for (const s of loaded) sessions[s.id] = s;
      set((s) => ({
        referenceLap: reference,
        comparisonLaps: comparisons,
        trackId: sessions[reference.sessionId]?.trackId ?? view.trackId,
        sessions: { ...s.sessions, ...sessions },
        graphMode: view.graphMode,
        mapColourMode: view.mapColourMode,
        channelConfig: view.channelConfig.map((e) => ({ ...e })),
        zoom: view.zoom,
        highlight: view.highlight,
        error: null,
      }));
      // Eagerly load session summaries for every lap so lap times show up
      // (in legends, chips, timeline) without needing to expand each session.
      await Promise.all(sessionIds.map((id) => get().ensureSummary(id)));
      await resampleAll(get, set);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  currentViewState: () => {
    const s = get();
    if (!s.trackId || !s.referenceLap) return null;
    return {
      trackId: s.trackId,
      referenceLap: s.referenceLap,
      comparisonLaps: s.comparisonLaps,
      zoom: s.zoom,
      highlight: s.highlight,
      channelConfig: s.channelConfig,
      graphMode: s.graphMode,
      mapColourMode: s.mapColourMode,
    };
  },
}));

type Get = typeof useWorkspaceStore.getState;
type Set = (partial: Partial<WorkspaceState>) => void;

/**
 * Build the channel config from the union of channels across loaded laps if it
 * is empty, applying the default visible set. Existing config is preserved.
 */
async function ensureChannelConfig(get: Get, set: Set): Promise<void> {
  const state = get();
  const laps = allLaps(state);
  if (laps.length === 0) return;
  const sessions = await Promise.all(laps.map((l) => loadSession(l.sessionId)));
  const channels = channelUnion(sessions);

  const meta: Record<string, { name: string; unit: string; group: string }> = {};
  for (const ch of channels) meta[ch.id] = { name: ch.name, unit: ch.unit, group: ch.group };

  const existing = new Map(state.channelConfig.map((c) => [c.channelId, c]));
  const config: ChannelConfigEntry[] = channels.map((ch, i) => {
    const prev = existing.get(ch.id);
    if (prev) return prev;
    const defaultIdx = DEFAULT_VISIBLE.indexOf(ch.id);
    return {
      channelId: ch.id,
      visible: defaultIdx >= 0,
      order: defaultIdx >= 0 ? defaultIdx : DEFAULT_VISIBLE.length + i,
    };
  });
  set({ channelConfig: config, channelMeta: meta });
}

/** Resample every lap in the workspace for the currently visible channels. */
async function resampleAll(get: Get, set: Set): Promise<void> {
  const state = get();
  const laps = allLaps(state);
  if (laps.length === 0) {
    set({ resampled: {} });
    return;
  }
  await ensureChannelConfig(get, set);
  const channelIds = visibleChannelIds(get().channelConfig);
  // Always include the segment-detection channels so the events timeline works
  // even when those channels are hidden from the graph stack.
  const resampleIds = [...new Set([...channelIds, ...SEGMENT_CHANNELS])];
  const entries = await Promise.all(
    laps.map(async (ref) => {
      const r = await resampleLapRef(ref, resampleIds);
      return [lapKey(ref), r] as const;
    }),
  );
  const resampled: Record<string, ResampledLap> = {};
  for (const [key, r] of entries) {
    if (r) resampled[key] = r;
  }
  set({ resampled });
}
